import { FormFieldType } from '@doss/prisma/types'
import * as yup from 'yup'
import { actionDataSchema } from './action-data.js'

// Schemas

export const signatureFormFieldMetadataSchema = yup
  .object({
    // Optional allows backwards compatibility with existing signature fields
    linkUserToSignature: yup.boolean().optional(),
  })
  .nullable()
  .optional()

type SignatureFormFieldMetadata = yup.InferType<typeof signatureFormFieldMetadataSchema>

export const shortTextFormFieldMetadataSchema = yup
  .object({
    // Optional allows backwards compatibility with existing text fields
    isMobileBarcode: yup.boolean().optional(),
    isScannerBarcode: yup.boolean().optional(),
  })
  .nullable()
  .optional()

type ShortTextFormFieldMetadata = yup.InferType<typeof shortTextFormFieldMetadataSchema>

export type FormFieldMetadata = SignatureFormFieldMetadata | ShortTextFormFieldMetadata

/**
 * Checks if the given metadata is valid for the given form field type.
 */
export const isValidFormFieldMetadata = (
  metadata: unknown,
  formFieldType: FormFieldType,
): metadata is FormFieldMetadata => {
  switch (formFieldType) {
    case FormFieldType.SIGNATURE:
      return signatureFormFieldMetadataSchema.isValidSync(metadata)
    case FormFieldType.SHORT_TEXT:
      return shortTextFormFieldMetadataSchema.isValidSync(metadata)
    case FormFieldType.BOOLEAN:
    case FormFieldType.CHECKBOXES:
    case FormFieldType.DATE:
    case FormFieldType.DATE_TIME:
    case FormFieldType.DROPDOWN:
    case FormFieldType.EMAIL:
    case FormFieldType.FILE:
    case FormFieldType.LOCATION:
    case FormFieldType.LONG_TEXT:
    case FormFieldType.NUMBER:
    case FormFieldType.RADIO_BUTTON:
      return metadata === null || metadata === undefined
  }
}

export const formFieldLookupCellDataSchema = yup.object({
  cellId: yup.string().required(),
  rowId: yup.string().required(),
  resolvedCellId: yup.string().nullable(),
  resolvedRowId: yup.string().nullable(),
})

export const formFieldDataSchema = yup
  .object({
    name: yup.string().required(),
    order: yup.number().required(),
    value: actionDataSchema.nullable().defined(),
    isArray: yup.boolean().required(),
    isOverwritingLookup: yup.boolean().required(),
    lookupCells: yup.array().of(formFieldLookupCellDataSchema).nullable(),
    lookupCellIds: yup
      .array()
      .of(yup.string())
      .nullable()
      .when('isArray', {
        is: true,
        then: (schema) => schema.defined(),
        otherwise: (schema) => schema.notRequired(),
      }),
    lookupColumnId: yup.string().nullable(),
    type: yup.string().oneOf(Object.keys(FormFieldType)).required(),
    formattedValue: yup.string().nullable().defined(),
    shouldLinkUserToSignature: yup.boolean().optional(),
    isMobileBarcode: yup.boolean().optional(),
    isScannerBarcode: yup.boolean().optional(),

    // DEPRECATED
    lookupCellId: yup
      .string()
      .nullable()
      .when('isArray', {
        is: false,
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.notRequired(),
      }),
    lookupRowId: yup
      .string()
      .nullable()
      .when('isArray', {
        is: false,
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.notRequired(),
      }),
    lookupRowIds: yup
      .array()
      .of(yup.string())
      .nullable()
      .when('isArray', {
        is: true,
        then: (schema) => schema.defined(),
        otherwise: (schema) => schema.notRequired(),
      }),
  })
  .strict()

export type FormFields = { [fieldId: string]: FormFieldData }
export const formCategoryDataSchema = yup
  .object({
    name: yup.string().nullable().defined(),
    order: yup.number().required(),
    fields: yup
      .array()
      .of(
        yup
          .mixed<FormFields>((value): value is FormFields => {
            return Object.values(value).every((field) => formFieldDataSchema.isValidSync(field))
          })
          .required(),
      )
      .required(),
  })
  .strict()

export type FormCategories = { [categoryId: string]: FormCategoryData }
export const formPageDataSchema = yup
  .object({
    name: yup.string().required(),
    order: yup.number().required(),
    categories: yup
      .mixed<FormCategories>((value): value is FormCategories => {
        return Object.values(value).every((category) =>
          formCategoryDataSchema.isValidSync(category),
        )
      })
      .required(),
  })
  .strict()

export type FormPages = { [pageId: string]: FormPageData }
export const formDataSchema = yup
  .object({
    formId: yup.string().required(),
    name: yup.string().required(),
    pages: yup
      .mixed<FormPages>((value): value is FormPages => {
        return Object.values(value).every((page) => formPageDataSchema.isValidSync(page))
      })
      .required(),
    attachmentIds: yup.array().of(yup.string().required()).nullable(),
  })
  .strict()

// Inferred types

/** Data submitted in an individual field within a form category. */
export type FormFieldData = yup.InferType<typeof formFieldDataSchema>

/** Data submitted in a category on a form page. */
export type FormCategoryData = yup.InferType<typeof formCategoryDataSchema>

/** Data submitted on a single page of a form. */
export type FormPageData = yup.InferType<typeof formPageDataSchema>

/** Data submitted through a form. */
export type FormData = yup.InferType<typeof formDataSchema>
